import {
  BackButton,
  Container,
  LoadingIndicator,
  Page,
  PageTitle,
  useToaster,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import { useOrganization } from '@foxtrotplatform/platform-micro-site-core';
import { Button } from '@windmill/react-ui';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  SolutionInputFields,
  SolutionVersionDTO,
} from '../../common/api/models/solution';
import { editSolution, fetchSolution } from '../../common/api/solution';
import { requiredFieldsFilled } from '../../common/utilities/requiredFieldsFilled';
import { getLatestSolutionVersion } from './getLatestSolutionVersion';
import { SolutionRegistrationInput } from './SolutionRegistrationInput';
import { solutionTabs } from './solutionSteps';
import { SolutionSubmissionTabs } from './SolutionSubmissionTabs';

export const EditSolution: React.FunctionComponent = () => {
  const { addToast } = useToaster();
  const navigate = useNavigate();
  const { currentOrg } = useOrganization();
  const { solutionId } = useParams();
  const [isRegistering, setIsRegistering] = useState(false);
  const [newestSolution, setNewestSolution] = useState<SolutionVersionDTO>();

  useEffect(() => {
    if (!currentOrg || !solutionId) {
      return;
    }

    const executeQuery = async () => {
      const solution = await fetchSolution(currentOrg.id, solutionId);

      setNewestSolution(getLatestSolutionVersion(solution.versions));
    };
    executeQuery();
  }, [solutionId]);

  const { register, control, handleSubmit, reset, watch, formState } =
    useForm<SolutionInputFields>({
      mode: 'onBlur',
      defaultValues: useMemo(() => {
        return newestSolution;
      }, [newestSolution]),
    });

  useEffect(() => {
    reset(newestSolution);
  }, [newestSolution]);

  const watchAll = watch();
  const required = ['displayName', 'shortDescription', 'categories'];
  const notFullyFilledOut = !requiredFieldsFilled(watchAll, required);
  const onSubmit = async (data: SolutionInputFields) => {
    setIsRegistering(true);

    if (currentOrg && currentOrg.id && newestSolution) {
      try {
        await editSolution(
          currentOrg.id,
          newestSolution.solutionId,
          newestSolution.solutionVersionId,
          data
        );
        addToast({
          title: 'Success!',
          message: 'Your solution has been edited.',
          type: 'success',
        });
        navigate(`/solution/${newestSolution.solutionId}`);
      } catch {
        addToast({
          message: 'Failed to edit solution.',
          type: 'error',
        });
        setIsRegistering(false);
      }
    }

    setIsRegistering(false);
  };

  if (!newestSolution) {
    return (
      <Page>
        <Container>
          <div className="w-full flex flex-row p-6 justify-center">
            <LoadingIndicator />
          </div>
        </Container>
      </Page>
    );
  }

  return (
    <Page>
      <Container size="medium">
        <div className="w-full flex flex-col justify-between items-center mb-os1-spacing">
          <div className="w-full flex flex-row items-center gap-x-3">
            <BackButton />
            <PageTitle>{newestSolution?.displayName}</PageTitle>
          </div>
          <div className="w-full mt-6">
            <SolutionSubmissionTabs
              activeTab="register"
              disabled={['define', 'artifacts']}
              tabs={solutionTabs}
            />
          </div>
        </div>
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <SolutionRegistrationInput
              control={control}
              createButNotEditFields={['version']}
              formState={formState}
              register={register}
            />
            <div className="w-full flex flex-row p-4 bg-gray-100">
              {isRegistering ? (
                <div className="w-full flex flex-row p-6 justify-center">
                  <LoadingIndicator />
                </div>
              ) : (
                <Button
                  disabled={
                    Object.keys(formState.errors).length > 0 ||
                    notFullyFilledOut
                  }
                  type="submit"
                >
                  Save Changes
                </Button>
              )}
            </div>
          </form>
        </div>
      </Container>
    </Page>
  );
};
